import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { FaCheckCircle, FaWater, FaSeedling, FaChartLine, FaUsers, FaAward, FaLeaf, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import ProductCard from '../components/ProductCard';
import { products as staticProducts } from '../data/products';


const Home = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentSlide, setCurrentSlide] = useState(0);

  const heroImages = [
    'https://images.unsplash.com/photo-1623211265502-412854043d15?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzR8MHwxfHNlYXJjaHwzfHxpbmRpYW4lMjBmYXJtZXIlMjBzbWlsaW5nJTIwaW4lMjBmaWVsZCUyMHdpdGglMjBkcmlwJTIwaXJyaWdhdGlvbnxlbnwwfHx8fDE3NzE2MTY1MTJ8MA&ixlib=rb-4.1.0&q=85',
    'https://images.unsplash.com/photo-1623211269755-569fec0536d2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzR8MHwxfHNlYXJjaHw0fHxpbmRpYW4lMjBmYXJtZXIlMjBzbWlsaW5nJTIwaW4lMjBmaWVsZCUyMHdpdGglMjBkcmlwJTIwaXJyaWdhdGlvbnxlbnwwfHx8fDE3NzE2MTY1MTJ8MA&ixlib=rb-4.1.0&q=85',
    'https://images.pexels.com/photos/20458078/pexels-photo-20458078.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
    'https://images.pexels.com/photos/35067651/pexels-photo-35067651.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940'
  ];

  useEffect(() => {
    // Use static products
    setProducts(staticProducts.slice(0, 3));
    setLoading(false);
    
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroImages.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % heroImages.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + heroImages.length) % heroImages.length);
  };

  const features = [
    {
      icon: <FaWater className="text-4xl" />,
      title: 'Water Conservation',
      description: 'Save up to 70% water compared to traditional irrigation methods'
    },
    {
      icon: <FaSeedling className="text-4xl" />,
      title: 'Better Crop Yield',
      description: 'Increase your harvest by 30-50% with precision irrigation'
    },
    {
      icon: <FaChartLine className="text-4xl" />,
      title: 'Cost Effective',
      description: 'Reduce labor and electricity costs while maximizing returns'
    },
    {
      icon: <FaLeaf className="text-4xl" />,
      title: 'Eco-Friendly',
      description: 'Sustainable farming with minimal environmental impact'
    }
  ];

  const stats = [
    { number: '10,000+', label: 'Happy Farmers', icon: <FaUsers /> },
    { number: '15+', label: 'Years Experience', icon: <FaAward /> },
    { number: '50+', label: 'Product Range', icon: <FaSeedling /> },
    { number: '98%', label: 'Success Rate', icon: <FaCheckCircle /> }
  ];

  const testimonials = [
    {
      name: 'Ramesh Kumar',
      location: 'Karnataka',
      text: 'These drip systems transformed my farm. Water savings are incredible and my crops never looked better!',
      rating: 5
    },
    {
      name: 'Priya Sharma',
      location: 'Maharashtra',
      text: 'Best investment for my vegetable farm. The quality of products and after-sales support is excellent.',
      rating: 5
    },
    {
      name: 'Suresh Patel',
      location: 'Gujarat',
      text: 'Increased my cotton yield by 40%. The drip irrigation system pays for itself in one season!',
      rating: 5
    }
  ];

  return (
    <div className="min-h-screen" data-testid="home-page">
      {/* Hero Section with Carousel */}
      <section className="relative min-h-[90vh] flex items-center justify-center overflow-hidden" data-testid="hero-section">
        {heroImages.map((image, index) => (
          <div
            key={index}
            className={`absolute inset-0 bg-cover bg-center transition-opacity duration-1000 ${
              index === currentSlide ? 'opacity-100' : 'opacity-0'
            }`}
            style={{ backgroundImage: `url('${image}')` }}
          >
            <div className="absolute inset-0 bg-[#2E368F]/60"></div>
          </div>
        ))}

        {/* Carousel Controls */}
        <button
          onClick={prevSlide}
          className="absolute left-4 z-20 bg-white/20 hover:bg-white/30 text-white p-3 rounded-full backdrop-blur-sm transition-all"
          aria-label="Previous slide"
        >
          <FaChevronLeft className="text-2xl" />
        </button>
        <button
          onClick={nextSlide}
          className="absolute right-4 z-20 bg-white/20 hover:bg-white/30 text-white p-3 rounded-full backdrop-blur-sm transition-all"
          aria-label="Next slide"
        >
          <FaChevronRight className="text-2xl" />
        </button>

        <div className="relative z-10 max-w-5xl mx-auto px-6 lg:px-8 text-center text-white">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-tight text-white" data-testid="hero-title">
            <span className="text-white">Transform Your Farm with</span>
            <br />
            <span className="text-[#6ABD45]">Smart Drip Irrigation</span>
          </h1>
          <p className="text-xl sm:text-2xl mb-8 text-gray-200 max-w-3xl mx-auto">
            Save water, increase yield, and grow sustainably with India's trusted drip irrigation solutions
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Link
              to="/products"
              className="bg-[#6ABD45] text-white hover:bg-[#5AAC35] rounded-full px-8 py-4 font-semibold shadow-lg hover:shadow-xl transition-all text-lg"
              data-testid="explore-products-button"
            >
              Explore Products
            </Link>
            <a
              href="https://wa.me/919448650161"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-white text-[#2E368F] border-2 border-white hover:bg-gray-100 rounded-full px-8 py-4 font-semibold transition-all text-lg"
              data-testid="get-quote-button"
            >
              Get Free Quote
            </a>
          </div>
        </div>

        {/* Slide Indicators */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-20 flex space-x-2">
          {heroImages.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentSlide(index)}
              className={`w-3 h-3 rounded-full transition-all ${
                index === currentSlide ? 'bg-white w-8' : 'bg-white/50'
              }`}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      </section>

      {/* Features Section - Clean White Background */}
      <section className="py-32 bg-white" data-testid="features-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-20">
            <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 text-[#2E368F]">Why Choose Drip Irrigation?</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto leading-relaxed">
              Modern farming demands smart solutions. Discover the benefits that make drip irrigation essential for today's farmers.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <div
                key={index}
                className="bg-white p-10 rounded-lg border border-gray-200 transition-all duration-300 hover:border-[#6ABD45] hover:shadow-lg hover:-translate-y-1"
                data-testid={`feature-card-${index}`}
              >
                <div className="text-[#6ABD45] mb-6 transition-transform duration-300 hover:scale-110">
                  {feature.icon}
                </div>
                <h3 className="font-semibold text-xl mb-4 text-[#2E368F]">{feature.title}</h3>
                <p className="text-gray-600 leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section - Solid Royal Indigo */}
      <section className="py-24 bg-[#2E368F] text-white" data-testid="stats-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-12">
            {stats.map((stat, index) => (
              <div key={index} className="text-center" data-testid={`stat-item-${index}`}>
                <div className="text-5xl mb-4 flex justify-center text-[#6ABD45]">
                  {stat.icon}
                </div>
                <div className="text-5xl lg:text-6xl font-bold mb-3 display-text">{stat.number}</div>
                <div className="text-lg text-white/80">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Products - Light Gray Background */}
      <section className="py-32 bg-gray-50" data-testid="featured-products-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-20">
            <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 text-[#2E368F]">Featured Products</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto leading-relaxed">
              Explore our range of high-quality drip irrigation products designed for Indian farming conditions.
            </p>
          </div>

          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-16 w-16 border-4 border-[#6ABD45] border-t-transparent"></div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
              {products.map((product) => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
          )}

          <div className="text-center mt-16">
            <Link
              to="/products"
              className="inline-block bg-[#6ABD45] text-white hover:bg-[#5AAC35] rounded-lg px-10 py-4 font-semibold transition-all duration-250 hover:shadow-lg hover:-translate-y-0.5"
              data-testid="view-all-products-button"
            >
              View All Products
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-24 bg-white" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4 text-[#2E368F]">What Farmers Say</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Real experiences from farmers who transformed their farming with our drip irrigation systems.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div
                key={index}
                className="bg-gray-50 p-8 rounded-2xl border border-gray-200 hover:border-[#6ABD45] transition-all"
                data-testid={`testimonial-${index}`}
              >
                <div className="flex mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <svg key={i} className="w-5 h-5 text-yellow-500 fill-current" viewBox="0 0 20 20">
                      <path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z" />
                    </svg>
                  ))}
                </div>
                <p className="text-gray-600 mb-6 italic">"{testimonial.text}"</p>
                <div className="border-t border-gray-200 pt-4">
                  <p className="font-bold text-[#2E368F]">{testimonial.name}</p>
                  <p className="text-sm text-gray-500">{testimonial.location}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#2E368F] text-white" data-testid="cta-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-6">Ready to Transform Your Farm?</h2>
          <p className="text-xl mb-8 text-white">
            Get in touch with our experts today for a free consultation and customized solution for your farm.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="bg-white text-[#2E368F] hover:bg-gray-100 rounded-full px-8 py-4 font-semibold transition-all text-lg"
              data-testid="contact-us-button"
            >
              Contact Us
            </Link>
            <a
              href="https://wa.me/919448650161"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-[#25D366] text-white hover:bg-[#20BA5A] rounded-full px-8 py-4 font-semibold transition-all text-lg flex items-center justify-center gap-2"
              data-testid="whatsapp-cta-button"
            >
              <FaCheckCircle />
              WhatsApp Now
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;